/**
 * Localization.
 *
 * Arabic strings are written as Arabic, not machine-translated from English —
 * including the finance vocabulary, where a literal translation reads wrong
 * ("الرصيد المتبقي" for remaining, not "المتبقي" alone). RTL is handled by the
 * document `dir` attribute plus logical CSS properties, so no string carries
 * layout responsibility.
 */

export type Locale = 'en' | 'ar';

const en = {
  appName: 'Fintrack',
  tagline: 'Your money, understood automatically.',

  // Navigation
  navHome: 'Home',
  navTransactions: 'Activity',
  navPlan: 'Plan',
  navInsights: 'Insights',
  navAssistant: 'Ask',
  navGoals: 'Goals',
  navSettings: 'Settings',

  // Auth
  signIn: 'Sign in',
  signUp: 'Create account',
  email: 'Email',
  password: 'Password',
  name: 'Name',
  nameOptional: 'Name (optional)',
  forgotPassword: 'Forgot password?',
  noAccount: "Don't have an account?",
  haveAccount: 'Already have an account?',
  passwordHint: 'At least 10 characters',
  signingIn: 'Signing in…',
  creatingAccount: 'Creating account…',

  // Onboarding
  welcomeTitle: "Let's understand your money automatically.",
  welcomeBody: 'Fintrack reads your bank notifications, sorts them, and builds your financial picture on its own. Four short questions and you are done.',
  getStarted: 'Get started',
  country: 'Country',
  currency: 'Currency',
  howTransactionsArrive: 'Which phone are you using?',
  sourceHelp: 'Fintrack reads the alerts your bank already sends you. How it gets them off your phone depends on which one it is.',
  sourceNoBankLogin: 'Either way, Fintrack never asks for your bank login and never connects to your account. It only reads the messages already on your phone.',
  monthlyIncome: 'Monthly income',
  monthlyIncomeOptional: 'Monthly income (optional)',
  incomeHelp: 'Only used to work out what you can spend. You can skip this.',
  savingsGoalOptional: 'Savings goal (optional)',
  goalName: 'What are you saving for?',
  goalTarget: 'Target amount',
  goalDate: 'Target date',
  skip: 'Skip',
  finish: 'Finish',
  back: 'Back',
  next: 'Next',
  step: 'Step',
  of: 'of',

  // Sources
  platformIphone: 'iPhone',
  platformIphoneDesc: 'iOS lets no app read your messages — Apple\u2019s rule, not a missing feature. You set up a Shortcut once, and after that bank alerts arrive on their own. We walk you through it.',
  platformAndroid: 'Android',
  platformAndroidDesc: 'The Fintrack app reads bank alerts on the phone and sends them here. Nothing else is read.',
  sourceManual: 'Add manually',

  // Dashboard
  thisMonth: 'This month',
  lastMonth: 'Last month',
  today: 'Today',
  thisWeek: 'This week',
  last30: 'Last 30 days',
  last90: 'Last 90 days',
  last6Months: 'Last 6 months',
  thisYear: 'This year',
  allTime: 'All time',
  spent: 'Spent',
  income: 'Income',
  remaining: 'Remaining',
  remainingHelp: 'Income minus spending this period. This is not an account balance.',
  netFlow: 'Net',
  dailyAverage: 'Daily average',
  spendingBreakdown: 'Where it went',
  topMerchants: 'Most spent at',
  recentActivity: 'Recent activity',
  seeAll: 'See all',
  noSpendingYet: 'No spending recorded yet',
  vsLastMonth: 'vs last month',
  previousPeriod: 'Previous',

  // Transactions
  transactions: 'Transactions',
  allTransactions: 'All transactions',
  search: 'Search merchant or description',
  filters: 'Filters',
  clearFilters: 'Clear',
  category: 'Category',
  subcategory: 'Subcategory',
  merchant: 'Merchant',
  amount: 'Amount',
  date: 'Date',
  type: 'Type',
  account: 'Account',
  paymentMethod: 'Payment method',
  source: 'Source',
  notes: 'Notes',
  addTransaction: 'Add transaction',
  editTransaction: 'Edit transaction',
  transactionDetails: 'Transaction',
  save: 'Save',
  cancel: 'Cancel',
  delete: 'Delete',
  edit: 'Edit',
  editProfile: 'Edit profile',
  editAccount: 'Edit account',
  editGoal: 'Edit goal',
  institution: 'Bank',
  accountType: 'Type',
  emailLocked: 'Your email is how you sign in, so it cannot be changed here.',
  currencyChangeHelp: 'Changing this does not convert existing transactions — each keeps the currency it was recorded in.',
  deleteGoalConfirm: 'Delete this goal?',
  applyToSimilar: 'Use this for future transactions from this merchant',
  applyToSimilarHelp: 'Fintrack will remember your choice and apply it automatically.',
  howCategorized: 'How this was categorized',
  otherAtMerchant: 'Other transactions here',
  needsReview: 'Needs review',
  reviewQueue: 'Needs your attention',
  confirmCorrect: 'This is correct',
  markDuplicate: 'It is a duplicate',
  noTransactions: 'No transactions yet',
  noTransactionsBody: 'Once a bank message arrives, or you add one yourself, it will appear here.',

  // Types
  typeExpense: 'Expense',
  typeIncome: 'Income',
  typeTransfer: 'Transfer',
  typeRefund: 'Refund',
  typeFee: 'Fee',

  // Insights
  insights: 'Insights',
  noInsightsYet: 'Not enough data yet',
  noInsightsBody: 'A few weeks of transactions and the patterns will start showing up here.',
  subscriptions: 'Subscriptions',
  recurringCommitments: 'Recurring commitments',
  perMonth: 'per month',
  perYear: 'per year',
  nextCharge: 'Next',
  lastCharge: 'Last',
  notASubscription: 'Not a subscription',
  estimatedAnnual: 'Estimated yearly',

  // Provenance — the trust vocabulary
  confirmed: 'Confirmed',
  inferred: 'Inferred',
  estimated: 'Estimated',
  confirmedHelp: 'Taken directly from your transactions.',
  inferredHelp: 'Fintrack worked this out and may be wrong.',
  estimatedHelp: 'A projection from past behaviour, not a confirmed figure.',
  aiSuggested: 'AI',

  // Assistant
  assistant: 'Ask your money',
  askPlaceholder: 'Ask about your spending…',
  assistantIntro: 'Ask anything about your money. Every answer is worked out from your actual transactions.',
  thinking: 'Looking at your transactions…',
  basedOn: 'Based on',
  transactionsLower: 'transactions',
  showEvidence: 'Show the transactions',
  hideEvidence: 'Hide',
  deterministicMode: 'Running without an AI model — answers come from the same database queries, with fixed wording.',

  // Affordability
  canIAfford: 'Can I afford it?',
  affordPrompt: 'How much are you thinking of spending?',
  checkIt: 'Check',
  likelyAffordable: 'This looks affordable',
  tight: 'This would be tight',
  likelyUnaffordable: 'This looks difficult',
  insufficientData: 'Not enough information',
  assumptions: 'What this assumes',
  limitations: 'What is missing',
  notAdvice: 'This is an estimate from your recorded transactions, not financial advice.',
  projectedSurplus: 'Projected surplus this month',
  afterPurchase: 'After this purchase',
  accountBalance: 'Account balance',
  notAvailable: 'Not available',

  // Budgets & goals
  budgets: 'Budgets',
  goals: 'Goals',
  createBudget: 'Create budget',
  suggestedBudgets: 'Suggested for you',
  suggestedHelp: 'Based on what you usually spend. Nothing is created until you accept it.',
  accept: 'Accept',
  budgetOf: 'of',
  savingsGoals: 'Savings goals',
  createGoal: 'New goal',
  target: 'Target',
  saved: 'Saved',
  requiredMonthly: 'Needed each month',
  onTrack: 'On track',
  offTrack: 'Behind',
  targetDate: 'Target date',

  // Alerts
  alerts: 'Alerts',
  noAlerts: 'Nothing needs your attention',
  dismiss: 'Dismiss',

  // Settings
  settings: 'Settings',
  profile: 'Profile',
  language: 'Language',
  accounts: 'Accounts & cards',
  transactionSources: 'Transaction sources',
  privacy: 'Privacy & data',
  security: 'Security',
  notifications: 'Notifications',
  pushNotifications: 'Alerts on your lock screen',
  sendTestNotification: 'Send a test notification',
  turnOn: 'Turn on',
  turnOff: 'Turn off',
  faceIdSignIn: 'Face ID sign-in',
  unlock: 'Unlock',
  setUpFaceId: 'Set up Face ID',
  addAnotherDevice: 'Add another device',
  signInWithPasskey: 'Sign in with Face ID',
  alertSettings: 'Alert settings',
  exportData: 'Export my data',
  exportHelp: 'Download everything Fintrack holds about you as a JSON file.',
  deleteData: 'Delete my financial data',
  deleteDataHelp: 'Removes every transaction but keeps your account.',
  deleteAccount: 'Delete my account permanently',
  deleteAccountHelp: 'Removes your account and all data. This cannot be undone.',
  changePassword: 'Change password',
  currentPassword: 'Current password',
  newPassword: 'New password',
  signOut: 'Sign out',
  signOutAll: 'Sign out on all devices',
  aiConsent: 'Allow AI processing',
  aiConsentHelp: 'When on, transactions that cannot be recognised automatically may be sent to an AI model for classification. Merchant names only — never your identity or account numbers.',
  importCsv: 'Import CSV',
  addSource: 'Add a source',
  ingestKey: 'Device key',
  connectPhoneTitle: 'One last thing: connect your phone',
  connectPhoneBody: 'Your account is ready. Nothing will arrive until this is done \u2014 it takes a few minutes and you only do it once.',
  connectPhoneLater: 'I\u2019ll do this later',
  ingestKeyHelpIos: 'Your iPhone Shortcut sends this with every message. Shown only once.',
  ingestKeyHelpAndroid: 'Paste this into the Fintrack Android app. Shown only once.',
  ingestNextIos: 'Next: Settings \u2192 Set up on iPhone walks you through building the Shortcut. Nothing arrives until that is done.',
  ingestNextAndroid: 'Next: install the Fintrack Android app and paste the key in. Nothing arrives until that is done.',
  copy: 'Copy',
  copied: 'Copied',
  rotateKey: 'Replace key',
  typeConfirm: 'Type',
  toConfirm: 'to confirm',

  // Import
  csvImport: 'Import transactions',
  chooseFile: 'Choose a CSV file',
  csvHelp: 'Export a statement from your bank as CSV and upload it here. You will see exactly what would be imported before anything is saved.',
  detectedColumns: 'Detected columns',
  importable: 'Ready to import',
  duplicatesFound: 'Duplicates found',
  failedRows: 'Could not be read',
  importNow: 'Import',
  importing: 'Importing…',
  importedCount: 'Imported',
  previewNote: 'Nothing is saved until you press Import.',

  // Paste a message
  pasteMessage: 'Paste a bank message',
  pasteHelp: 'Paste an SMS or notification and Fintrack will show you exactly what it reads from it.',
  parseIt: 'Read it',
  parsedResult: 'What Fintrack found',
  notATransaction: 'This does not look like a transaction',
  saveTransaction: 'Save it',

  // Common
  loading: 'Loading…',
  retry: 'Try again',
  somethingWrong: 'Something went wrong',
  close: 'Close',
  done: 'Done',
  optional: 'optional',
  yes: 'Yes',
  no: 'No',
  none: 'None',
  transactionsCount: 'transactions',
};

type Dict = typeof en;

const ar: Dict = {
  appName: 'فينتراك',
  tagline: 'أموالك، مفهومة تلقائياً.',

  navHome: 'الرئيسية',
  navTransactions: 'الحركات',
  navPlan: 'الخطة',
  navInsights: 'رؤى',
  navAssistant: 'اسأل',
  navGoals: 'الأهداف',
  navSettings: 'الإعدادات',

  signIn: 'تسجيل الدخول',
  signUp: 'إنشاء حساب',
  email: 'البريد الإلكتروني',
  password: 'كلمة المرور',
  name: 'الاسم',
  nameOptional: 'الاسم (اختياري)',
  forgotPassword: 'نسيت كلمة المرور؟',
  noAccount: 'ليس لديك حساب؟',
  haveAccount: 'لديك حساب بالفعل؟',
  passwordHint: '١٠ أحرف على الأقل',
  signingIn: 'جارٍ تسجيل الدخول…',
  creatingAccount: 'جارٍ إنشاء الحساب…',

  welcomeTitle: 'لنفهم أموالك تلقائياً.',
  welcomeBody: 'يقرأ فينتراك إشعارات بنكك، يصنّفها، ويبني صورتك المالية بنفسه. أربعة أسئلة قصيرة وتنتهي.',
  getStarted: 'لنبدأ',
  country: 'الدولة',
  currency: 'العملة',
  howTransactionsArrive: 'أي هاتف تستخدم؟',
  sourceHelp: 'يقرأ فينتراك التنبيهات التي يرسلها بنكك أصلاً. وطريقة وصولها من هاتفك تعتمد على نوعه.',
  sourceNoBankLogin: 'في الحالتين، لا يطلب فينتراك بيانات دخولك البنكية ولا يتصل بحسابك. يقرأ فقط الرسائل الموجودة على هاتفك.',
  monthlyIncome: 'الدخل الشهري',
  monthlyIncomeOptional: 'الدخل الشهري (اختياري)',
  incomeHelp: 'يُستخدم فقط لحساب ما يمكنك إنفاقه. يمكنك تخطي هذا.',
  savingsGoalOptional: 'هدف الادخار (اختياري)',
  goalName: 'لأجل ماذا تدّخر؟',
  goalTarget: 'المبلغ المستهدف',
  goalDate: 'التاريخ المستهدف',
  skip: 'تخطي',
  finish: 'إنهاء',
  back: 'رجوع',
  next: 'التالي',
  step: 'خطوة',
  of: 'من',

  platformIphone: 'آيفون',
  platformIphoneDesc: 'لا يسمح iOS لأي تطبيق بقراءة رسائلك — وهذا قرار من آبل لا نقص في التطبيق. لذلك تُعدّ اختصاراً (Shortcut) مرة واحدة، وبعدها تصل تنبيهات بنكك تلقائياً. سنرشدك خطوة بخطوة.',
  platformAndroid: 'أندرويد',
  platformAndroidDesc: 'يقرأ تطبيق فينتراك تنبيهات البنك على الهاتف ويرسلها هنا. ولا يقرأ شيئاً غيرها.',
  sourceManual: 'إضافة يدوية',

  thisMonth: 'هذا الشهر',
  lastMonth: 'الشهر الماضي',
  today: 'اليوم',
  thisWeek: 'هذا الأسبوع',
  last30: 'آخر ٣٠ يوماً',
  last90: 'آخر ٩٠ يوماً',
  last6Months: 'آخر ٦ أشهر',
  thisYear: 'هذه السنة',
  allTime: 'كل الفترات',
  spent: 'المصروف',
  income: 'الدخل',
  remaining: 'المتبقي',
  remainingHelp: 'الدخل ناقص المصروف في هذه الفترة. هذا ليس رصيد حسابك.',
  netFlow: 'الصافي',
  dailyAverage: 'المعدل اليومي',
  spendingBreakdown: 'أين ذهبت',
  topMerchants: 'أكثر ما أنفقت لديه',
  recentActivity: 'آخر الحركات',
  seeAll: 'عرض الكل',
  noSpendingYet: 'لا يوجد إنفاق مسجل بعد',
  vsLastMonth: 'مقارنة بالشهر الماضي',
  previousPeriod: 'السابقة',

  transactions: 'الحركات',
  allTransactions: 'كل الحركات',
  search: 'ابحث عن تاجر أو وصف',
  filters: 'تصفية',
  clearFilters: 'مسح',
  category: 'الفئة',
  subcategory: 'الفئة الفرعية',
  merchant: 'التاجر',
  amount: 'المبلغ',
  date: 'التاريخ',
  type: 'النوع',
  account: 'الحساب',
  paymentMethod: 'وسيلة الدفع',
  source: 'المصدر',
  notes: 'ملاحظات',
  addTransaction: 'إضافة حركة',
  editTransaction: 'تعديل الحركة',
  transactionDetails: 'الحركة',
  save: 'حفظ',
  cancel: 'إلغاء',
  delete: 'حذف',
  edit: 'تعديل',
  editProfile: 'تعديل الملف الشخصي',
  editAccount: 'تعديل الحساب',
  editGoal: 'تعديل الهدف',
  institution: 'البنك',
  accountType: 'النوع',
  emailLocked: 'بريدك الإلكتروني هو وسيلة تسجيل الدخول، لذلك لا يمكن تغييره من هنا.',
  currencyChangeHelp: 'تغيير العملة لا يحوّل الحركات السابقة — تبقى كل حركة بالعملة التي سُجّلت بها.',
  deleteGoalConfirm: 'حذف هذا الهدف؟',
  applyToSimilar: 'استخدم هذا للحركات القادمة من هذا التاجر',
  applyToSimilarHelp: 'سيتذكر فينتراك اختيارك ويطبّقه تلقائياً.',
  howCategorized: 'كيف تم التصنيف',
  otherAtMerchant: 'حركات أخرى هنا',
  needsReview: 'بحاجة لمراجعة',
  reviewQueue: 'بحاجة إلى انتباهك',
  confirmCorrect: 'هذا صحيح',
  markDuplicate: 'إنها مكررة',
  noTransactions: 'لا توجد حركات بعد',
  noTransactionsBody: 'بمجرد وصول رسالة من بنكك، أو إضافتك حركة بنفسك، ستظهر هنا.',

  typeExpense: 'مصروف',
  typeIncome: 'دخل',
  typeTransfer: 'تحويل',
  typeRefund: 'استرداد',
  typeFee: 'رسوم',

  insights: 'رؤى',
  noInsightsYet: 'لا توجد بيانات كافية بعد',
  noInsightsBody: 'بعد أسابيع قليلة من الحركات ستبدأ الأنماط بالظهور هنا.',
  subscriptions: 'الاشتراكات',
  recurringCommitments: 'الالتزامات المتكررة',
  perMonth: 'شهرياً',
  perYear: 'سنوياً',
  nextCharge: 'القادم',
  lastCharge: 'الأخير',
  notASubscription: 'ليس اشتراكاً',
  estimatedAnnual: 'التقدير السنوي',

  confirmed: 'مؤكد',
  inferred: 'مستنتج',
  estimated: 'تقديري',
  confirmedHelp: 'مأخوذ مباشرة من حركاتك.',
  inferredHelp: 'استنتجه فينتراك وقد يكون خاطئاً.',
  estimatedHelp: 'توقّع بناءً على سلوكك السابق، وليس رقماً مؤكداً.',
  aiSuggested: 'ذكاء اصطناعي',

  assistant: 'اسأل عن أموالك',
  askPlaceholder: 'اسأل عن إنفاقك…',
  assistantIntro: 'اسأل أي شيء عن أموالك. كل إجابة محسوبة من حركاتك الفعلية.',
  thinking: 'أراجع حركاتك…',
  basedOn: 'بناءً على',
  transactionsLower: 'حركة',
  showEvidence: 'اعرض الحركات',
  hideEvidence: 'إخفاء',
  deterministicMode: 'يعمل بدون نموذج ذكاء اصطناعي — الإجابات من نفس استعلامات قاعدة البيانات، بصياغة ثابتة.',

  canIAfford: 'هل أستطيع تحمّل ذلك؟',
  affordPrompt: 'كم تفكر أن تنفق؟',
  checkIt: 'تحقّق',
  likelyAffordable: 'يبدو أن هذا في متناولك',
  tight: 'سيكون هذا ضيقاً',
  likelyUnaffordable: 'يبدو أن هذا صعب',
  insufficientData: 'لا توجد معلومات كافية',
  assumptions: 'ما يفترضه هذا',
  limitations: 'ما هو ناقص',
  notAdvice: 'هذا تقدير من حركاتك المسجلة، وليس نصيحة مالية.',
  projectedSurplus: 'الفائض المتوقع هذا الشهر',
  afterPurchase: 'بعد هذه العملية',
  accountBalance: 'رصيد الحساب',
  notAvailable: 'غير متاح',

  budgets: 'الميزانيات',
  goals: 'الأهداف',
  createBudget: 'إنشاء ميزانية',
  suggestedBudgets: 'مقترحة لك',
  suggestedHelp: 'بناءً على إنفاقك المعتاد. لا يتم إنشاء شيء حتى توافق.',
  accept: 'موافقة',
  budgetOf: 'من',
  savingsGoals: 'أهداف الادخار',
  createGoal: 'هدف جديد',
  target: 'الهدف',
  saved: 'المدّخر',
  requiredMonthly: 'المطلوب شهرياً',
  onTrack: 'على المسار',
  offTrack: 'متأخر',
  targetDate: 'التاريخ المستهدف',

  alerts: 'التنبيهات',
  noAlerts: 'لا شيء يحتاج انتباهك',
  dismiss: 'تجاهل',

  settings: 'الإعدادات',
  profile: 'الملف الشخصي',
  language: 'اللغة',
  accounts: 'الحسابات والبطاقات',
  transactionSources: 'مصادر الحركات',
  privacy: 'الخصوصية والبيانات',
  security: 'الأمان',
  notifications: 'الإشعارات',
  pushNotifications: 'التنبيهات على شاشة القفل',
  sendTestNotification: 'إرسال إشعار تجريبي',
  turnOn: 'تفعيل',
  turnOff: 'إيقاف',
  faceIdSignIn: 'الدخول ببصمة الوجه',
  unlock: 'فتح القفل',
  setUpFaceId: 'إعداد Face ID',
  addAnotherDevice: 'إضافة جهاز آخر',
  signInWithPasskey: 'الدخول بـ Face ID',
  alertSettings: 'إعدادات التنبيهات',
  exportData: 'تصدير بياناتي',
  exportHelp: 'حمّل كل ما يحتفظ به فينتراك عنك كملف JSON.',
  deleteData: 'حذف بياناتي المالية',
  deleteDataHelp: 'يحذف كل الحركات ويبقي حسابك.',
  deleteAccount: 'حذف حسابي نهائياً',
  deleteAccountHelp: 'يحذف حسابك وكل بياناتك. لا يمكن التراجع عن هذا.',
  changePassword: 'تغيير كلمة المرور',
  currentPassword: 'كلمة المرور الحالية',
  newPassword: 'كلمة المرور الجديدة',
  signOut: 'تسجيل الخروج',
  signOutAll: 'تسجيل الخروج من كل الأجهزة',
  aiConsent: 'السماح بمعالجة الذكاء الاصطناعي',
  aiConsentHelp: 'عند التفعيل، قد تُرسل الحركات التي يتعذّر التعرف عليها تلقائياً إلى نموذج ذكاء اصطناعي لتصنيفها. أسماء التجار فقط — لا هويتك ولا أرقام حساباتك.',
  importCsv: 'استيراد CSV',
  addSource: 'إضافة مصدر',
  ingestKey: 'مفتاح الجهاز',
  connectPhoneTitle: 'خطوة أخيرة: اربط هاتفك',
  connectPhoneBody: 'حسابك جاهز. لن يصل شيء قبل إتمام هذه الخطوة \u2014 تستغرق دقائق وتُنجز مرة واحدة فقط.',
  connectPhoneLater: 'سأفعل ذلك لاحقاً',
  ingestKeyHelpIos: 'يرسله اختصار الآيفون مع كل رسالة. يُعرض مرة واحدة فقط.',
  ingestKeyHelpAndroid: 'الصق هذا في تطبيق فينتراك على أندرويد. يُعرض مرة واحدة فقط.',
  ingestNextIos: 'الخطوة التالية: الإعدادات ← الإعداد على الآيفون، وهناك شرح لإنشاء الاختصار. لن يصل شيء قبل إتمامه.',
  ingestNextAndroid: 'الخطوة التالية: ثبّت تطبيق فينتراك على أندرويد والصق المفتاح. لن يصل شيء قبل إتمامه.',
  copy: 'نسخ',
  copied: 'تم النسخ',
  rotateKey: 'استبدال المفتاح',
  typeConfirm: 'اكتب',
  toConfirm: 'للتأكيد',

  csvImport: 'استيراد الحركات',
  chooseFile: 'اختر ملف CSV',
  csvHelp: 'صدّر كشف حساب من بنكك بصيغة CSV وارفعه هنا. سترى بالضبط ما سيُستورد قبل حفظ أي شيء.',
  detectedColumns: 'الأعمدة المكتشفة',
  importable: 'جاهز للاستيراد',
  duplicatesFound: 'مكررات',
  failedRows: 'تعذّرت قراءتها',
  importNow: 'استيراد',
  importing: 'جارٍ الاستيراد…',
  importedCount: 'تم استيراد',
  previewNote: 'لا يُحفظ شيء حتى تضغط استيراد.',

  pasteMessage: 'الصق رسالة بنكية',
  pasteHelp: 'الصق رسالة أو إشعاراً وسيعرض فينتراك بالضبط ما يقرأه منها.',
  parseIt: 'اقرأها',
  parsedResult: 'ما وجده فينتراك',
  notATransaction: 'لا تبدو هذه حركة مالية',
  saveTransaction: 'احفظها',

  loading: 'جارٍ التحميل…',
  retry: 'حاول مرة أخرى',
  somethingWrong: 'حدث خطأ ما',
  close: 'إغلاق',
  done: 'تم',
  optional: 'اختياري',
  yes: 'نعم',
  no: 'لا',
  none: 'لا شيء',
  transactionsCount: 'حركة',
};

const DICTS: Record<Locale, Dict> = { en, ar };

export type TranslationKey = keyof Dict;

export function translate(locale: Locale, key: TranslationKey): string {
  return DICTS[locale][key] ?? DICTS.en[key] ?? key;
}

export function isRtl(locale: Locale): boolean {
  return locale === 'ar';
}

/** Applies direction and language to the document, once per locale change. */
export function applyDocumentLocale(locale: Locale): void {
  document.documentElement.lang = locale;
  document.documentElement.dir = isRtl(locale) ? 'rtl' : 'ltr';
}

/**
 * API errors, translated by their code.
 *
 * The server composes its messages in English — it has no locale on most of
 * these paths, and the ones it does have would still mean duplicating every
 * sentence in two places. Every error already carries a stable `code`, which is
 * the part worth translating against: the code is the contract, the message is
 * one rendering of it.
 *
 * Anything not listed falls back to the server's own text. That is deliberate —
 * an English sentence that describes the problem beats a generic Arabic one
 * that does not, and it means a new error code degrades rather than breaks.
 */
const ERRORS_AR: Record<string, string> = {
  not_found: 'لم يُعثر على العنصر.',
  forbidden: 'ليس لديك صلاحية لهذا الإجراء.',
  unauthenticated: 'انتهت جلستك. سجّل الدخول من جديد.',
  session_revoked: 'أُلغيت هذه الجلسة. سجّل الدخول من جديد.',
  invalid_credentials: 'البريد الإلكتروني أو كلمة المرور غير صحيحة.',
  email_taken: 'هذا البريد الإلكتروني مُسجَّل بالفعل.',
  registration_closed: 'التسجيل مغلق حالياً.',
  validation_error: 'بعض الحقول غير صحيحة.',
  rate_limited: 'محاولات كثيرة جداً. انتظر قليلاً ثم أعد المحاولة.',
  conflict: 'هذا السجل موجود بالفعل.',
  invalid_reference: 'السجل المُشار إليه غير موجود.',
  network_error: 'تعذّر الوصول إلى الخادم. تحقّق من اتصالك.',
  invalid_category: 'فئة غير معروفة.',
  unknown_category: 'فئة غير معروفة.',
  invalid_subcategory: 'الفئة الفرعية لا تنتمي إلى تلك الفئة.',
  category_exists: 'هذه الفئة موجودة بالفعل.',
  unsupported_currency: 'هذه العملة غير مدعومة.',
  invalid_range: 'يجب أن يكون تاريخ النهاية بعد تاريخ البداية.',
  invalid_json: 'تعذّرت قراءة البيانات المُرسلة.',
  body_too_large: 'المحتوى المُرسل كبير جداً.',
  empty_file: 'الملف فارغ.',
  too_many_rows: 'الملف يحتوي على صفوف أكثر مما يمكن استيراده دفعة واحدة.',
  invalid_refresh: 'انتهت صلاحية جلستك. سجّل الدخول من جديد.',
  invalid_reset_token: 'رابط إعادة التعيين غير صالح أو منتهي الصلاحية.',
  expired: 'انتهت الصلاحية.',
  preview_expired: 'انتهت صلاحية هذه المعاينة. أعد المحاولة.',
  challenge_expired: 'انتهت مهلة المحاولة. حاول مرة أخرى.',
  bad_passkey: 'تعذّر التحقق من مفتاح المرور.',
  unknown_passkey: 'مفتاح المرور هذا غير معروف.',
  passkey_exists: 'مفتاح المرور هذا مُسجَّل بالفعل.',
  passkey_unverified: 'تعذّر التحقق من مفتاح المرور.',
  invalid_ingest_key: 'مفتاح الإدخال غير صالح.',
  missing_ingest_key: 'مفتاح الإدخال مفقود.',
  source_paused: 'هذا المصدر متوقف مؤقتاً.',
  create_failed: 'تعذّر الإنشاء. حاول مرة أخرى.',
  ambiguous_savings_target: 'حدّد مبلغاً أو نسبة، وليس الاثنين معاً.',
  bad_request: 'الطلب غير صالح.',
};

/**
 * The message to show for a failed request.
 *
 * Falls through to whatever the server said when the code has no translation,
 * and to the raw message when there is no code at all.
 */
export function translateError(
  locale: Locale,
  error: { code?: string; message: string },
): string {
  if (locale !== 'ar') return error.message;
  return (error.code && ERRORS_AR[error.code]) || error.message;
}
